// Phase 1024b — Complete Admin Users page with per-user permissions.
//
// Features:
//   - Search by name/email
//   - Add User (name, email, password, role)
//   - Role selector (admin / staff / building_payments_clerk)
//   - Manage Permissions (grant/deny per-module checkboxes)
//   - Reset Password
//   - Activate / Deactivate (session revocation on deactivate)
//   - Soft Delete (confirmation)
//   - View audit log
// All server-side protected; this file is the UX surface only.

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UserPlus, MagnifyingGlass, Shield, Trash, Key, Pencil, Clock, LockOpen, DeviceMobileSlash } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { v: 'admin', label: 'Admin' },
  { v: 'staff', label: 'Staff' },
  { v: 'building_payments_clerk', label: 'Building Payments Clerk' },
];

const roleLabel = (r) => (ROLES.find((x) => x.v === r)?.label || r || '-');

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [defaults, setDefaults] = useState({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Modals
  const [addOpen, setAddOpen] = useState(false);
  const [permsOpen, setPermsOpen] = useState(false);
  const [permsUser, setPermsUser] = useState(null);
  const [permsGrant, setPermsGrant] = useState([]);
  const [permsDeny, setPermsDeny] = useState([]);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetPw, setResetPw] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState([]);

  const [addForm, setAddForm] = useState({ name: '', email: '', password: '', role: 'staff' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ures, cres] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/users/permission-catalog`, { withCredentials: true }),
      ]);
      setUsers(ures.data || []);
      setCatalog(cres.data?.catalog || {});
      setDefaults(cres.data?.role_defaults || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => (u.email || '').toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q));
  }, [users, search]);

  // ---- Add user ----
  const submitAdd = async () => {
    if (!addForm.email || !addForm.password || !addForm.name) { toast.error('All fields required'); return; }
    if (addForm.password.length < 8) { toast.error('Password ≥ 8 chars'); return; }
    try {
      await axios.post(`${API}/auth/register`, addForm, { withCredentials: true });
      toast.success('User created');
      setAddOpen(false);
      setAddForm({ name: '', email: '', password: '', role: 'staff' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    }
  };

  // ---- Role change ----
  const changeRole = async (u, role) => {
    if (role === u.role) return;
    try {
      await axios.put(`${API}/users/${u.user_id}/role?role=${encodeURIComponent(role)}`, {}, { withCredentials: true });
      toast.success('Role updated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Role change failed');
    }
  };

  // ---- Permissions ----
  const openPerms = (u) => {
    setPermsUser(u);
    setPermsGrant((u.permissions?.grant || []).slice());
    setPermsDeny((u.permissions?.deny || []).slice());
    setPermsOpen(true);
  };
  const togglePerm = (perm, kind) => {
    if (kind === 'grant') {
      setPermsGrant((p) => p.includes(perm) ? p.filter((x) => x !== perm) : [...p, perm]);
      setPermsDeny((p) => p.filter((x) => x !== perm));
    } else {
      setPermsDeny((p) => p.includes(perm) ? p.filter((x) => x !== perm) : [...p, perm]);
      setPermsGrant((p) => p.filter((x) => x !== perm));
    }
  };
  const savePerms = async () => {
    try {
      await axios.put(`${API}/users/${permsUser.user_id}/permissions`,
        { grant: permsGrant, deny: permsDeny }, { withCredentials: true });
      toast.success('Permissions updated');
      setPermsOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  // ---- Status toggle ----
  const toggleStatus = async (u) => {
    const next = u.status === 'deactivated' ? 'active' : 'deactivated';
    const confirmMsg = next === 'deactivated' ? `Deactivate ${u.email}? Their sessions will be revoked.` : `Reactivate ${u.email}?`;
    setConfirmAction({ title: 'Change user status', msg: confirmMsg, run: async () => {
      try {
        await axios.put(`${API}/users/${u.user_id}/status`, { status: next }, { withCredentials: true });
        toast.success(`User ${next}`);
        load();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed');
      }
    }});
    setConfirmOpen(true);
  };

  // ---- Delete ----
  const askDelete = (u) => {
    setConfirmAction({
      title: 'Delete user',
      msg: `Delete ${u.email}? The account record is kept for audit integrity, but all sessions are revoked and the user can no longer log in. This cannot be undone from the UI.`,
      run: async () => {
        try {
          await axios.delete(`${API}/users/${u.user_id}`, { withCredentials: true });
          toast.success('User deleted');
          load();
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Delete failed');
        }
      },
    });
    setConfirmOpen(true);
  };

  const isLocked = (u) => !!u.locked_until && new Date(u.locked_until) > new Date();
  const userAction = async (u, action, msg) => {
    try {
      await axios.post(`${API}/users/${u.user_id}/${action}`, {}, { withCredentials: true });
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Αποτυχία');
    }
  };

  // ---- Reset password ----
  const openReset = (u) => { setResetUser(u); setResetPw(''); setResetOpen(true); };
  const submitReset = async () => {
    if (resetPw.length < 8) { toast.error('Password ≥ 8 chars'); return; }
    try {
      await axios.post(`${API}/users/${resetUser.user_id}/admin-reset-password`,
        { new_password: resetPw }, { withCredentials: true });
      toast.success('Password reset');
      setResetOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    }
  };

  // ---- Audit ----
  const openAudit = async () => {
    try {
      const { data } = await axios.get(`${API}/users/audit`, { withCredentials: true });
      setAudit(data || []);
      setAuditOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load audit');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 p-6">
      <div className="max-w-6xl mx-auto">
        <button onClick={() => navigate('/dashboard')} className="text-sm text-zinc-600 hover:text-zinc-900 flex items-center gap-1 mb-3" data-testid="admusers-back">
          <ArrowLeft size={14} /> Dashboard
        </button>
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-xl font-semibold flex items-center gap-2" data-testid="admusers-title"><Shield size={20} /> Admin Users</h1>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-none" onClick={openAudit} data-testid="admusers-audit"><Clock size={14} className="mr-1"/> Audit</Button>
            <Button className="rounded-none bg-zinc-950 hover:bg-zinc-800" onClick={() => setAddOpen(true)} data-testid="admusers-add"><UserPlus size={14} className="mr-1"/> Add User</Button>
          </div>
        </div>
        <div className="relative mb-3">
          <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-zinc-400"/>
          <Input placeholder="Search by name or email…" value={search} onChange={(e)=>setSearch(e.target.value)} className="pl-7 rounded-none" data-testid="admusers-search" />
        </div>
        <div className="bg-white border border-zinc-200">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs text-zinc-500 uppercase">
              <tr>
                <th className="text-left p-2">Name</th>
                <th className="text-left p-2">Email</th>
                <th className="text-left p-2">Role</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Permissions</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="p-4 text-center text-zinc-500">Loading…</td></tr>}
              {!loading && filtered.map((u) => (
                <tr key={u.user_id} className="border-t border-zinc-100" data-testid={`user-row-${u.user_id}`}>
                  <td className="p-2 font-medium">{u.name}</td>
                  <td className="p-2 text-zinc-600">{u.email}</td>
                  <td className="p-2">
                    <Select value={u.role} onValueChange={(v)=>changeRole(u, v)}>
                      <SelectTrigger className="h-8 w-44 rounded-none text-xs"><SelectValue>{roleLabel(u.role)}</SelectValue></SelectTrigger>
                      <SelectContent>
                        {ROLES.map(r => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-2">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] ${u.status === 'deactivated' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>{u.status || 'active'}</span>
                    {isLocked(u) && <span className="ml-1 px-2 py-0.5 rounded-full text-[11px] bg-amber-100 text-amber-800" data-testid={`locked-badge-${u.user_id}`}>Κλειδωμένος</span>}
                  </td>
                  <td className="p-2 text-xs text-zinc-500">{(u.effective_permissions || []).length} active</td>
                  <td className="p-2 text-right">
                    <div className="flex gap-1 justify-end flex-wrap">
                      <Button variant="ghost" size="sm" onClick={()=>openPerms(u)} data-testid={`perms-${u.user_id}`}><Pencil size={14}/></Button>
                      <Button variant="ghost" size="sm" onClick={()=>openReset(u)} data-testid={`reset-${u.user_id}`}><Key size={14}/></Button>
                      {isLocked(u) && <Button variant="ghost" size="sm" className="text-amber-700" onClick={()=>userAction(u, 'unlock', 'Ο χρήστης ξεκλειδώθηκε')} data-testid={`unlock-${u.user_id}`}><LockOpen size={14} className="mr-1"/>Ξεκλείδωμα</Button>}
                      <Button variant="ghost" size="sm" title="Αποσύνδεση από όλες τις συσκευές" onClick={()=>{ if (window.confirm(`Αποσύνδεση του ${u.email} από όλες τις συσκευές;`)) userAction(u, 'logout-all', 'Ο χρήστης αποσυνδέθηκε παντού'); }} data-testid={`logout-all-${u.user_id}`}><DeviceMobileSlash size={14}/></Button>
                      <Button variant="ghost" size="sm" onClick={()=>toggleStatus(u)} data-testid={`status-${u.user_id}`}>{u.status === 'deactivated' ? 'Activate' : 'Deactivate'}</Button>
                      <Button variant="ghost" size="sm" className="text-emerald-700" onClick={async ()=>{
                        try { await axios.post(`${API}/users/${u.user_id}/approve`, {}, { withCredentials: true }); toast.success('Approved & reactivated'); load(); }
                        catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
                      }} data-testid={`approve-${u.user_id}`}>Approve</Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={()=>askDelete(u)} data-testid={`delete-${u.user_id}`}><Trash size={14}/></Button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="p-4 text-center text-zinc-500">No users</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={addForm.name} onChange={(e)=>setAddForm({...addForm, name:e.target.value})} data-testid="add-user-name"/></div>
            <div><Label>Email</Label><Input type="email" value={addForm.email} onChange={(e)=>setAddForm({...addForm, email:e.target.value})} data-testid="add-user-email"/></div>
            <div><Label>Initial Password</Label><Input type="password" value={addForm.password} onChange={(e)=>setAddForm({...addForm, password:e.target.value})} data-testid="add-user-pw"/>
              <p className="text-[11px] text-zinc-400 mt-1">User can change via Account page. ≥8 chars, letters+digits.</p>
            </div>
            <div><Label>Role</Label>
              <Select value={addForm.role} onValueChange={(v)=>setAddForm({...addForm, role: v})}>
                <SelectTrigger className="rounded-none"><SelectValue>{roleLabel(addForm.role)}</SelectValue></SelectTrigger>
                <SelectContent>{ROLES.map(r=><SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={()=>setAddOpen(false)}>Cancel</Button>
            <Button className="rounded-none bg-zinc-950 hover:bg-zinc-800" onClick={submitAdd} data-testid="add-user-save">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permissions — Phase 1024d hierarchical UI */}
      <Dialog open={permsOpen} onOpenChange={setPermsOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Permissions — {permsUser?.email}</DialogTitle></DialogHeader>
          <p className="text-xs text-zinc-500 mb-3">
            Role default: <b>{roleLabel(permsUser?.role)}</b>. Each module has a Grant / Deny Access toggle;
            when access is granted, you can further restrict each section with per-action checkboxes.
            Explicit denies always win over role defaults and over module access grants.
          </p>
          <div className="space-y-3">
            {Object.entries(catalog).map(([module, meta]) => {
              const moduleAccessPerm = `${module}.access`;
              const moduleDefault = (defaults[permsUser?.role] || []).includes(moduleAccessPerm);
              const moduleGranted = permsGrant.includes(moduleAccessPerm);
              const moduleDenied = permsDeny.includes(moduleAccessPerm);
              const moduleEffective = moduleGranted || (moduleDefault && !moduleDenied);
              const sections = meta.sections || {};
              return (
                <div key={module} className={`border p-3 ${moduleEffective ? 'border-emerald-300 bg-emerald-50/30' : 'border-zinc-200 bg-zinc-50/30'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="font-semibold text-sm">{meta.label || module}</div>
                      <div className="text-[10px] text-zinc-500">default: {moduleDefault ? 'granted' : 'denied'}</div>
                    </div>
                    <div className="flex gap-3 text-xs">
                      <label className="flex items-center gap-1"><input type="checkbox" checked={moduleGranted} onChange={()=>togglePerm(moduleAccessPerm,'grant')} data-testid={`mod-grant-${module}`}/>Grant Access</label>
                      <label className="flex items-center gap-1"><input type="checkbox" checked={moduleDenied} onChange={()=>togglePerm(moduleAccessPerm,'deny')} data-testid={`mod-deny-${module}`}/>Deny Access</label>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] ${moduleEffective ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{moduleEffective ? 'ALLOWED' : 'DENIED'}</span>
                    </div>
                  </div>
                  {moduleEffective && (
                    <div className="mt-2 border-t pt-2">
                      <div className="grid grid-cols-5 gap-1 text-[11px] uppercase text-zinc-400 mb-1 px-1">
                        <div>Section</div><div className="text-center">View</div><div className="text-center">Create</div><div className="text-center">Edit</div><div className="text-center">Delete</div>
                      </div>
                      {Object.entries(sections).map(([section, actions]) => (
                        <div key={section} className="grid grid-cols-5 gap-1 items-center text-xs py-1 border-t border-zinc-100">
                          <div className="font-mono text-[11px] pl-1">{section}</div>
                          {['view','create','edit','delete'].map(action => {
                            if (!actions.includes(action)) return <div key={action} className="text-center text-zinc-300">—</div>;
                            const perm = `${module}.${section}.${action}`;
                            const roleDefault = (defaults[permsUser?.role] || []).includes(perm);
                            const granted = permsGrant.includes(perm);
                            const denied = permsDeny.includes(perm);
                            const effective = (granted || (roleDefault && !denied));
                            return (
                              <div key={action} className="text-center">
                                <label className="inline-flex items-center gap-1 cursor-pointer">
                                  <input type="checkbox" checked={granted || (roleDefault && !denied)} onChange={()=>{
                                    if (effective) togglePerm(perm, 'deny');
                                    else togglePerm(perm, 'grant');
                                  }} data-testid={`perm-${perm}`}/>
                                  <span className={`text-[10px] ${effective ? 'text-emerald-700' : 'text-zinc-400'}`}>{effective ? '✓' : '×'}</span>
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                      {sections.sensitive !== undefined && null}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={()=>setPermsOpen(false)}>Cancel</Button>
            <Button className="rounded-none bg-zinc-950 hover:bg-zinc-800" onClick={savePerms} data-testid="perms-save">Save Permissions</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Reset Password — {resetUser?.email}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>New Password</Label>
            <Input type="password" value={resetPw} onChange={(e)=>setResetPw(e.target.value)} data-testid="reset-pw-input"/>
            <p className="text-[11px] text-zinc-400">The user's sessions will be revoked. Share the password securely out-of-band.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={()=>setResetOpen(false)}>Cancel</Button>
            <Button className="rounded-none bg-zinc-950 hover:bg-zinc-800" onClick={submitReset} data-testid="reset-pw-save">Reset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{confirmAction?.title}</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600">{confirmAction?.msg}</p>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={()=>setConfirmOpen(false)}>Cancel</Button>
            <Button className="rounded-none bg-red-600 hover:bg-red-700" onClick={async ()=>{ await confirmAction?.run?.(); setConfirmOpen(false); }} data-testid="confirm-run">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Admin Audit Log</DialogTitle></DialogHeader>
          <table className="w-full text-xs">
            <thead><tr className="text-left text-zinc-500"><th className="p-1">When</th><th className="p-1">Action</th><th className="p-1">Actor</th><th className="p-1">Target</th><th className="p-1">Extra</th></tr></thead>
            <tbody>
              {audit.map(a => (
                <tr key={a.audit_id} className="border-t">
                  <td className="p-1 whitespace-nowrap">{(a.at || '').slice(0, 19).replace('T', ' ')}</td>
                  <td className="p-1 font-mono">{a.action}</td>
                  <td className="p-1">{a.actor_email}</td>
                  <td className="p-1 font-mono text-[10px]">{a.target_user_id}</td>
                  <td className="p-1 text-zinc-500 truncate max-w-xs">{a.extra ? JSON.stringify(a.extra) : ''}</td>
                </tr>
              ))}
              {audit.length === 0 && <tr><td colSpan={5} className="p-3 text-center text-zinc-500">No entries</td></tr>}
            </tbody>
          </table>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={()=>setAuditOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
